import { Box, Flex, Icon, Text } from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { Link as RouterLink } from "@tanstack/react-router"
import { FiBriefcase, FiHome, FiUsers, FiTruck, FiList, FiDollarSign, FiGrid, FiClipboard, FiShoppingCart } from "react-icons/fi"
import { GrGroup, GrUserAdmin } from "react-icons/gr"
import type { IconType } from "react-icons/lib"

import { Providers } from "@/client"
import useAuth from "@/hooks/useAuth"
import { UserRole } from "@/types/permissions"
import { getRecentClients } from "@/utils/recentClients"

const superUserAdminItems: Item[] = [
  { icon: FiBriefcase, title: "Providers", path: "/providers", role: UserRole.SUPER_USER },
  { icon: GrUserAdmin, title: "Users", path: "/users", role: UserRole.SUPER_USER },
]

const providerAdminItems: Item[] = [
  { icon: FiGrid, title: "Provider Dashboard", path: "/provider/providerdashboard", role: UserRole.PROVIDER_GROUP_ADMIN },
  { icon: GrGroup, title: "Provider Groups", path: "/provider/providergroups/", role: UserRole.PROVIDER_ADMIN },
  { icon: FiUsers, title: "Provider Users", path: "/provider/providerusers/", role: UserRole.PROVIDER_GROUP_ADMIN },
  { icon: FiUsers, title: "Provider Clients", path: "/provider/providerclients/", role: UserRole.PROVIDER_GROUP_ADMIN},
  { icon: FiList, title: "Service Catalog", path: "/provider/servicecatalog", role: UserRole.PROVIDER_ADMIN },
  { icon: FiShoppingCart, title: "Purchase Catalog", path: "/provider/purchasecatalog", role: UserRole.PROVIDER_ADMIN },
  { icon: FiUsers, title: "Client Types", path: "/provider/clienttypes", role: UserRole.PROVIDER_ADMIN },
  { icon: FiUsers, title: "User Types", path: "/provider/providerusertypes", role: UserRole.PROVIDER_ADMIN },
  { icon: FiDollarSign, title: "Budget Categories", path: "/provider/budgetcategories", role: UserRole.PROVIDER_ADMIN },
  { icon: FiClipboard, title: "Surveys", path: "/provider/surveys", role: UserRole.PROVIDER_ADMIN },
]

const vendorAdminItems: Item[] = [
  { icon: FiTruck, title: "Vendors", path: "/vendors", role: UserRole.SUPER_USER },
]

const providerUserItems: Item[] = [
  { icon: FiHome, title: "My Dashboard", path: "/" },
  { icon: FiUsers, title: "Clients", path: "/clients"},
]


interface SidebarItemsProps {
  onClose?: () => void
}

interface Item {
  icon: IconType
  title: string
  path: string
  role?: UserRole
}

const SidebarItems = ({ onClose }: SidebarItemsProps) => {
  const { checkRole, isLoggedIn } = useAuth()

  const { data: providersData } = useQuery({
    queryKey: ["providers"],
    queryFn: () => Providers.readProviders(),
  })
  const hasProviders = (providersData?.data?.length ?? 0) > 0

  // Initialize selected provider only when needed, without overriding manual selection.
  if (isLoggedIn && hasProviders) {
    const selectedProviderId = sessionStorage.getItem("selectedProviderId")
    const selectedProviderExists = providersData!.data.some(
      (provider) => provider.id === selectedProviderId,
    )

    if (!selectedProviderExists) {
      const firstProvider = providersData!.data[0]
      if (firstProvider.id) sessionStorage.setItem("selectedProviderId", firstProvider.id)
      if (firstProvider.entityName) {
        sessionStorage.setItem("selectedProviderName", firstProvider.entityName)
      }
    } else if (!sessionStorage.getItem("selectedProviderName")) {
      const selectedProvider = providersData!.data.find((provider) => provider.id === selectedProviderId)
      if (selectedProvider?.entityName) {
        sessionStorage.setItem("selectedProviderName", selectedProvider.entityName)
      }
    }
  }
  

  const getVisibleItems = (sectionItems: Item[]) => {
    return sectionItems.filter((item) => {
      // Hide provider-scoped routes when no providers exist.
      if ((item.title === "Provider Groups" || item.title === "Provider Clients") && !hasProviders) {
        return false
      }

      if (!item.role) {
        return true
      }

      return checkRole(item.role)
    })
  }

  const renderItems = (sectionItems: Item[]) => {
    return sectionItems.map(({ icon, title, path }) => (
      <RouterLink key={title} to={path} onClick={onClose}>
        <Flex
          gap={4}
          px={4}
          py={2}
          _hover={{
            background: "gray.subtle",
          }}
          alignItems="center"
          fontSize="sm"
        >
          <Icon as={icon} alignSelf="center" />
          <Text ml={2}>{title}</Text>
        </Flex>
      </RouterLink>
    ))
  }

  const visibleSuperUserItems = getVisibleItems(superUserAdminItems)
  const visibleProviderAdminItems = getVisibleItems(providerAdminItems)
  const visibleProviderUserItems = getVisibleItems(providerUserItems)
  const visibleVendorItems = getVisibleItems(vendorAdminItems)
  const selectedProviderName = sessionStorage.getItem("selectedProviderName")?.trim()
  const recentClients = getRecentClients()

  return (
    <>
      {visibleSuperUserItems.length > 0 && (
        <>
          <Text fontSize="xs" px={4} py={2} fontWeight="bold">
            Super User Menu
          </Text>
          <Box>{renderItems(visibleSuperUserItems)}</Box>
        </>
      )}

      {visibleProviderAdminItems.length > 0 && (
        <>
          <Text fontSize="xs" px={4} py={2} fontWeight="bold">
            {selectedProviderName || "Provider Admin Menu"}
          </Text>
          <Box>{renderItems(visibleProviderAdminItems)}</Box>
        </>
      )}

      {visibleVendorItems.length > 0 && (
        <>
          <Text fontSize="xs" px={4} py={2} fontWeight="bold">
            Vendor Menu
          </Text>
          <Box>{renderItems(visibleVendorItems)}</Box>
        </>
      )}

      {visibleProviderUserItems.length > 0 && (
        <>
          <Text fontSize="xs" px={4} py={2} fontWeight="bold">
            Menu
          </Text>
          <Box>{renderItems(visibleProviderUserItems)}</Box>
        </>
      )}

      {recentClients.length > 0 && (
        <>
          <Text fontSize="xs" px={4} py={2} fontWeight="bold">
            Recent Clients
          </Text>
          <Box>
            {recentClients.map((c) => (
              <RouterLink
                key={c.id}
                to="/provider/providerclients/edit"
                search={{ providerClientId: c.id }}
                onClick={onClose}
              >
                <Flex
                  gap={4}
                  px={4}
                  py={2}
                  _hover={{ background: "gray.subtle" }}
                  alignItems="center"
                  fontSize="sm"
                >
                  <Icon as={FiUsers} alignSelf="center" flexShrink={0} />
                  <Box minW={0}>
                    <Text ml={2} truncate>{c.displayName}</Text>
                    <Text ml={2} fontSize="xs" color="gray.400">{c.providerClientId}</Text>
                  </Box>
                </Flex>
              </RouterLink>
            ))}
          </Box>
        </>
      )}
    </>
  )
}

export default SidebarItems
